/**
 * Single source of truth for indexable video metadata.
 *
 * Consumed by:
 *   - src/component/PortfolioRouteSEO.jsx  -> runtime VideoObject JSON-LD
 *   - scripts/prerender-all.mjs            -> VideoObject in the STATIC html
 *   - sitemap-generator.js                 -> <video:video> sitemap entries
 *
 * Extend with:  npm run videos:registry     (adds new videos, preserves edits)
 * Promote with: npm run videos:promote -- <slug> --name "..." --description "..."
 *
 * FIELD NOTES — read before editing:
 *   name          Unique per video. Never reuse across routes.
 *   description   Unique, >= 2 sentences, describes what is ON SCREEN.
 *   thumbnailUrl  Must be a real frame. Run: npm run videos:posters
 *   duration      ISO 8601 (PT1M12S). Filled in by the poster script.
 *   mainContent   true  = rendered via <VideoBlock> as the page's main content,
 *                         gets VideoObject + a sitemap entry.
 *                 false = decorative background loop. Deliberately NOT
 *                         submitted to Google. Do not flip this by hand without
 *                         also giving the video a poster, controls, and a
 *                         visible heading + copy on the page.
 */

export const VIDEO_THUMB_BASE = "https://abox.agency/assets/image/video-thumbs"

export const portfolioVideosByPath = {
  "/": [
    {
      slug: "home-1",
      name: "Pour’d cocktails — Case Study Video by Abox Agency",
      description: "Case study video for Pour’d cocktails, produced by Abox Agency. From glass to screen, every interaction crafted perfectly. Covers the 3d Modeling, 3D Rendring, Motion video work delivered for the project.",
      contentUrl: "https://d3imeu5zcweet9.cloudfront.net/1756718785262_pour'd.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/cocktails.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-2",
      name: "Fruzo — Case Study Video by Abox Agency",
      description: "Case study video for Fruzo, produced by Abox Agency. Fresh flavours, freeze-dried goodness for everyday rituals. Covers the Branding, Packaging Design, Website work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/fruzo.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/fruzo.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-3",
      name: "Ras — Case Study Video by Abox Agency",
      description: "Case study video for Ras, produced by Abox Agency. Luxury elevated, where skincare meets artistry in every detail. Covers the 3d Modeling, 3D Rendring, Motion video work delivered for the project.",
      contentUrl: "https://d3imeu5zcweet9.cloudfront.net/1756718768709_ras.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/ras.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-4",
      name: "My Herb — Case Study Video by Abox Agency",
      description: "Case study video for My Herb, produced by Abox Agency. Herbal roots, modern remedies, renewed identity. Covers the Branding, Package Design, 3d Modeling, 3D Rendring work delivered for the project.",
      contentUrl: "https://d3imeu5zcweet9.cloudfront.net/1756718815261_myherb.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/my-herb.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-5",
      name: "Waylon Smith — Case Study Video by Abox Agency",
      description: "Case study video for Waylon Smith, produced by Abox Agency. Eyewear with attitude — designed to be worn daily. Covers the Branding, UI/UX Design, Website work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/waylon-smith.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/waylon-smith.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-6",
      name: "Uppercase — Case Study Video by Abox Agency",
      description: "Case study video for Uppercase, produced by Abox Agency. Shaping sustainable style into a digital trendsetter. Covers the Shopify Store Design and Development, CRO work delivered for the project.",
      contentUrl: "https://d3imeu5zcweet9.cloudfront.net/1756718704263_uppercase.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/uppercase.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-7",
      name: "Anandhaas Sweets — Case Study Video by Abox Agency",
      description: "Case study video for Anandhaas Sweets, produced by Abox Agency. The Taste of Royal India, perfected over generations. Covers the Branding, UI/UX Design, Website, CMS Platform work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/anandhaas-case-study.webm",
      thumbnailUrl: "https://abox.agency/assets/image/anandhaas.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-8",
      name: "Acer Electric — Case Study Video by Abox Agency",
      description: "Case study video for Acer Electric, produced by Abox Agency. Unlock the MAX factor — electric mobility with attitude. Covers the Branding, UI/UX Design, Website work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/acer-electric.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/acer-electric.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-9",
      name: "Oval — Case Study Video by Abox Agency",
      description: "Case study video for Oval, produced by Abox Agency. Modern personal care — science-led branding with clarity and warmth. Covers the Branding, Visual Identity, Packaging Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/oval.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/oval.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-10",
      name: "Eustress — Case Study Video by Abox Agency",
      description: "Case study video for Eustress, produced by Abox Agency. Still building — streetwear that pushes without breaking you. Covers the Branding, Visual Identity, Campaign Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Home-page-portfolios/eustress.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/eustress.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-11",
      name: "Elder Drinks — Case Study Video by Abox Agency",
      description: "Case study video for Elder Drinks, produced by Abox Agency. Light, refreshing, uplifting, flowing as smoothly as their drinks. Covers the 3d Modeling, 3D Rendring, Shopify Store Design and Development work delivered for the project.",
      contentUrl: "https://d3imeu5zcweet9.cloudfront.net/1756718881522_elder.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/homeportfolio/elder-drinks.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "home-12",
      name: "CreativeAgency PortfolioShowreel 6 — Case Study Video by Abox Agency",
      description: "Case study video for CreativeAgency PortfolioShowreel 6, produced by Abox Agency. Part of the ecommerce design and development work delivered for CreativeAgency PortfolioShowreel 6.",
      contentUrl: "https://d3imeu5zcweet9.cloudfront.net/1751611043730__CreativeAgency-PortfolioShowreel_6.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/meta_og_image.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/india-campaign": [
    {
      slug: "india-campaign",
      name: "Ecommerce Development Agency (India Campaign) — Case Study Video by Abox Agency",
      description: "Case study video for Ecommerce Development Agency, produced by Abox Agency. Part of the ecommerce design and development work delivered for Ecommerce Development Agency. Shown on the India Campaign project page at abox.agency/india-campaign.",
      contentUrl: "https://d1cihhbb125h48.cloudfront.net/1697548266.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/marketing/m_mobile.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/international-campaign": [
    {
      slug: "international-campaign",
      name: "Ecommerce Development Agency — Case Study Video by Abox Agency",
      description: "Case study video for Ecommerce Development Agency, produced by Abox Agency. Part of the ecommerce design and development work delivered for Ecommerce Development Agency.",
      contentUrl: "https://d1cihhbb125h48.cloudfront.net/1697548266.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/marketing/m_mobile.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/3d/fruzo": [
    {
      slug: "portfolio-3d-fruzo-1",
      name: "Cuba Charge — Case Study Video by Abox Agency",
      description: "Case study video for Cuba Charge, produced by Abox Agency. Fruzo sachet pair studio lifestyle left.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/Cuba%20Charge.webm",
      thumbnailUrl: null,
      uploadDate: "2026-09-26T09:29:19+05:30",
      duration: null,
      mainContent: false,
    },
    {
      slug: "portfolio-3d-fruzo-2",
      name: "Bali Balance — Case Study Video by Abox Agency",
      description: "Case study video for Bali Balance, produced by Abox Agency. Fruzo sachet pair studio lifestyle right.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/Bali%20Balance.webm",
      thumbnailUrl: null,
      uploadDate: "2026-09-26T09:29:19+05:30",
      duration: null,
      mainContent: false,
    },
    {
      slug: "portfolio-3d-fruzo-3",
      name: "Fruzo Pack — Case Study Video by Abox Agency",
      description: "Case study video for Fruzo Pack, produced by Abox Agency. Fruzo kraft mailer open showing red interior.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/fruzo-pack.mp4",
      thumbnailUrl: null,
      uploadDate: "2026-09-26T09:29:19+05:30",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/3d/myherb": [
    {
      slug: "portfolio-3d-myherb",
      name: "A camera can't\\nhold a colour. — Case Study Video by Abox Agency",
      description: "Case study video for A camera can't\\nhold a colour., produced by Abox Agency. My Herb 3D packaging and bottle hero presentation.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/myherb-hero.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/myherb-3d/slim-up-herbs.avif",
      uploadDate: "2026-09-26T09:29:19+05:30",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/3d/pourd": [
    {
      slug: "portfolio-3d-pourd",
      name: "One Bottle, Five Formulations — Case Study Video by Abox Agency",
      description: "Case study video for One Bottle, Five Formulations, produced by Abox Agency. Pour'd Whisky Sour bottle angled in raw rock formation.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/pourd.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/pourd-3d/pourd-11.avif",
      uploadDate: "2026-09-28T18:23:53+05:30",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/3d/ras": [
    {
      slug: "portfolio-3d-ras",
      name: "ras — Case Study Video by Abox Agency",
      description: "Case study video for ras, produced by Abox Agency. RAS lipstick standing against fluted glass with pure liquid drop.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/ras.webm",
      thumbnailUrl: null,
      uploadDate: "2026-09-28T18:23:53+05:30",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/3d/swara": [
    {
      slug: "portfolio-3d-swara",
      name: "The drawer, opening. — Case Study Video by Abox Agency",
      description: "Case study video for The drawer, opening., produced by Abox Agency. Swara Herbal Shake Deep Green drawer box angled product render.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/swara3d-drawer-opening.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/swara-3d/drawer-opening.webp",
      uploadDate: "2026-09-23T10:44:09+05:30",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/3d/within": [
    {
      slug: "portfolio-3d-within",
      name: "A camera can't\\nhold a colour. — Case Study Video by Abox Agency",
      description: "Case study video for A camera can't\\nhold a colour., produced by Abox Agency. Within 3D packaging and bottle hero presentation.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Abox%203D%20Portfolio/within-hero.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/within-3d/within-4.webp",
      uploadDate: "2026-09-28T18:23:53+05:30",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/acer-electric": [
    {
      slug: "portfolio-acer-electric",
      name: "Acer Electric sells motorcycles, scooters and e-cycles from one catalogue.\\nThe website was treating",
      description: "Case study video for Acer Electric sells motorcycles, scooters and e-cycles from one catalogue.\\nThe website was treating all three buyers as the same person., produced by Abox Agency. Acer Electric desktop and mobile storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/acer-electric.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/acer/device-preview.avif",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/agarwalbhavan-sweets": [
    {
      slug: "portfolio-agarwalbhavan-sweets",
      name: "Agarwal Bhavan Sweets — Shopify Case Study Video",
      description: "Case study video for Agarwal Bhavan Sweets, a brand in confectionery & sweets. Agarwal Bhavan Sweets wanted its traditional Indian sweets business online. Abox Agency built a Shopify store with custom branding to boost visibility and engagement. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Agarwal%20Bhavan%20Sweets.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/agarwal_sweets/agarwalsweets1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/anand-sweets": [
    {
      slug: "portfolio-anand-sweets",
      name: "Anand Sweets — Shopify Case Study Video",
      description: "Case study video for Anand Sweets, a brand in traditional sweets & confectionery. Anand Sweets needed a Shopify store that felt as premium as its mithai. Abox Agency delivered clean UI/UX design and on-site SEO to grow organic orders across India. Covers the Shopify, UI/UX Design and SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Anandsweets.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/og_images/anand_sweets.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/anandhaas": [
    {
      slug: "portfolio-anandhaas",
      name: "anandhaas case study — Case Study Video by Abox Agency",
      description: "Case study video for anandhaas case study, produced by Abox Agency. Shree Anandhaas desktop and mobile storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/anandhaas-case-study.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/anandhaas/device-preview.webp",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/arospice": [
    {
      slug: "portfolio-arospice",
      name: "Arospice — Shopify Case Study Video",
      description: "Case study video for Arospice, a brand in indian spices & millets. Arospice sells premium Indian spices and millets rooted in tradition. Abox Agency built a Shopify store with rich branding and graphic design for the D2C launch. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Arospice.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/arospice/arospice1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/aumex": [
    {
      slug: "portfolio-aumex",
      name: "Aumex — Shopify Case Study Video",
      description: "Case study video for Aumex, a brand in intimate hygiene & personal care. Aumex makes women's intimate hygiene products, including its V Wonder Wash line. Abox Agency built a Shopify store with careful branding and 3D product renders. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Aumex.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/aumex/aumex1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/ayurmedix": [
    {
      slug: "portfolio-ayurmedix",
      name: "Ayurmedix — Case Study Video by Abox Agency",
      description: "Case study video for Ayurmedix, produced by Abox Agency. Part of the ecommerce design and development work delivered for Ayurmedix.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Ayurmedix.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/ayurmedix/ayurmedix1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/bontrue-furniture": [
    {
      slug: "portfolio-bontrue-furniture",
      name: "Bontrue Furniture — Shopify Case Study Video",
      description: "Case study video for Bontrue Furniture, a brand in furniture & home decor. Bontrue Furniture sells stylish, functional pieces for modern homes. Abox Agency built a Shopify store with a gift registry, custom UI/UX, and on-site SEO. Covers the Shopify, UI/UX Design, Branding and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/BonTrue.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/bontrue_furniture/furniture1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/branding/70pc": [
    {
      slug: "portfolio-branding-70pc",
      name: "70pc — Case Study Video by Abox Agency",
      description: "Case study video for 70pc, produced by Abox Agency. Part of the ecommerce design and development work delivered for 70pc.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/70pc_branding/70pc1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/abox": [
    {
      slug: "portfolio-branding-abox",
      name: "Abox Agency — Visual Identity Case Study Video",
      description: "Case study video for Abox Agency, a brand in digital agency branding. Abox Agency's own visual identity needed to match its creative work. Explore our brand strategy, collateral, and customer experience process, applied to ourselves first. Covers the Visual Identity, Brand Strategy and Brand Collateral work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/abox/abox1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/fast-n-fry": [
    {
      slug: "portfolio-branding-fast-n-fry",
      name: "Fast n fry — Case Study Video by Abox Agency",
      description: "Case study video for Fast n fry, produced by Abox Agency. Part of the ecommerce design and development work delivered for Fast n fry.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/fastfry_branding/fastnfry1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/healing-hands": [
    {
      slug: "portfolio-branding-healing-hands",
      name: "Healinghands — Case Study Video by Abox Agency",
      description: "Case study video for Healinghands, produced by Abox Agency. Part of the ecommerce design and development work delivered for Healinghands.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/healinghand_branding/healinghand1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/jiva-ayush": [
    {
      slug: "portfolio-branding-jiva-ayush",
      name: "Jiva Ayush — Case Study Video by Abox Agency",
      description: "Case study video for Jiva Ayush, produced by Abox Agency. Part of the ecommerce design and development work delivered for Jiva Ayush.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/jivaayush_branding/jivaayush1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/nourysh": [
    {
      slug: "portfolio-branding-nourysh",
      name: "Nourysh — Visual Identity Case Study Video",
      description: "Case study video for Nourysh, a brand in health & wellness supplements. Nourysh needed a brand identity as science-backed as its supplements. Abox Agency built a new logo, guidelines, and 3D visuals that strengthened its wellness positioning. Covers the Visual Identity, Brand Strategy and Brand Collateral work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/nourysh_branding/nourysh1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/omnes": [
    {
      slug: "portfolio-branding-omnes",
      name: "Omnes — Case Study Video by Abox Agency",
      description: "Case study video for Omnes, produced by Abox Agency. Part of the ecommerce design and development work delivered for Omnes.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/omnes_branding/omnes1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/oval": [
    {
      slug: "portfolio-branding-oval",
      name: "Oval (Branding) — Case Study Video by Abox Agency",
      description: "Case study video for Oval, produced by Abox Agency. Part of the ecommerce design and development work delivered for Oval.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/oval_branding/oval1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/terahash-solutions": [
    {
      slug: "portfolio-branding-terahash-solutions",
      name: "Terahash Solutions — Visual Identity Case Study Video",
      description: "Case study video for Terahash Solutions, a brand in crypto mining hardware. Terahash Solutions needed a brand refresh for the Bitcoin mining industry. Abox Agency built a bold logo, cohesive guidelines, and 3D visuals for its market presence. Covers the Visual Identity, Brand Strategy and Brand Collateral work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/terahash_brand/terahash1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/tokyo-fix": [
    {
      slug: "portfolio-branding-tokyo-fix",
      name: "Tokyo fix — Case Study Video by Abox Agency",
      description: "Case study video for Tokyo fix, produced by Abox Agency. Part of the ecommerce design and development work delivered for Tokyo fix.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/tokyofix_branding/tokyofix1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/typical": [
    {
      slug: "portfolio-branding-typical",
      name: "Typical — Case Study Video by Abox Agency",
      description: "Case study video for Typical, produced by Abox Agency. Part of the ecommerce design and development work delivered for Typical.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/typical_branding/typical1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/branding/velora": [
    {
      slug: "portfolio-branding-velora",
      name: "velora — Case Study Video by Abox Agency",
      description: "Case study video for velora, produced by Abox Agency. Part of the ecommerce design and development work delivered for velora.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/branding.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/velora_branding/velora1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/chaykra": [
    {
      slug: "portfolio-chaykra",
      name: "Chaykra — Case Study Video by Abox Agency",
      description: "Case study video for Chaykra, produced by Abox Agency. Part of the ecommerce design and development work delivered for Chaykra.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Chaykra.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/chaykra/chaykra1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/dl-films": [
    {
      slug: "portfolio-dl-films",
      name: "DL Films — React JS Development Case Study Video",
      description: "Case study video for DL Films, a brand in photography & videography. DL Films tells stories through photography and cinematography. Abox Agency built a React JS portfolio site with branding and SEO fit for visual storytelling. Covers the React JS Development, UI/UX Design and Branding work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Dl.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/dlfilm/dlfilm2.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/dr-vaidya": [
    {
      slug: "portfolio-dr-vaidya",
      name: "Dr. Vaidya — Shopify Case Study Video",
      description: "Case study video for Dr. Vaidya, a brand in ayurvedic wellness & healthcare. Dr. Vaidya sells Ayurvedic stamina and health supplements online. Abox Agency built a Shopify storefront with custom graphics and UI/UX design to build customer trust. Covers the Shopify, UI/UX Design and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Dr.%20Vaidya.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/product/dr_vaidya/product.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/elder": [
    {
      slug: "portfolio-elder",
      name: "Elder — Case Study Video by Abox Agency",
      description: "Case study video for Elder, produced by Abox Agency. Part of the ecommerce design and development work delivered for Elder.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Elder.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/elder/elder1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/eustress": [
    {
      slug: "portfolio-eustress",
      name: "Most brands sell the arrival. — Case Study Video by Abox Agency",
      description: "Case study video for Most brands sell the arrival., produced by Abox Agency. Eustress desktop and mobile storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/eustress.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/eustress/eustress-1.avif",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/fast-n-fry": [
    {
      slug: "portfolio-fast-n-fry",
      name: "Fast n Fry — WordPress Development Case Study Video",
      description: "Case study video for Fast n Fry, a brand in frozen snacks manufacturing. Fast n Fry manufactures premium potato and frozen snacks. Abox Agency rebuilt its site on WordPress with fresh branding and UI/UX to revitalize its online presence. Covers the WordPress Development, UI/UX Design and Branding work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Fast%20n%20Fry.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/fastfry/fastfry1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/fone-network": [
    {
      slug: "portfolio-fone-network",
      name: "Fone Network — React JS Development Case Study Video",
      description: "Case study video for Fone Network, a brand in web3 & blockchain technology. Fone Network brings blockchain and NFT staking to mobile. Abox Agency built a React JS platform with branding and on-site SEO for this Web3 innovator. Covers the React JS Development, UI/UX Design, Branding and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Fone.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/fone/fone1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/fragstalk": [
    {
      slug: "portfolio-fragstalk-1",
      name: "Fragstalk — Case Study Video by Abox Agency",
      description: "Case study video for Fragstalk, produced by Abox Agency. Fragstalk desktop and mobile storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/fragstalk.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fragstalk/hero-ipad.png",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "portfolio-fragstalk-2",
      name: "Try before you commit — Case Study Video by Abox Agency",
      description: "Case study video for Try before you commit, produced by Abox Agency. Fragstalk mobile homepage, collection, product, and cart screens.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/coacl-eau-de.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fragstalk/build-01.png",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/freshleaf": [
    {
      slug: "portfolio-freshleaf",
      name: "FreshLeaf — Shopify Case Study Video",
      description: "Case study video for FreshLeaf, a brand in specialty tea. FreshLeaf sells pure, handpicked teas for tea enthusiasts. Abox Agency built its Shopify store with fresh branding and graphics that highlight product freshness. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Freshleaf.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/freshleaf/freshleaf2.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/fruzo": [
    {
      slug: "portfolio-fruzo-1",
      name: "Fruzo Main — Case Study Video by Abox Agency",
      description: "Case study video for Fruzo Main, produced by Abox Agency. Fruzo Shopify storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/fruzo-main.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fruzo/fruzo-poster.avif",
      uploadDate: "2026-09-02T17:04:16+05:30",
      duration: null,
      mainContent: false,
    },
    {
      slug: "portfolio-fruzo-2",
      name: "3D product renders — Case Study Video by Abox Agency",
      description: "Case study video for 3D product renders, produced by Abox Agency. Fruzo kraft shipping box 3D render.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/fruzo-pack.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fruzo/30-pack.webp",
      uploadDate: "2026-09-02T17:04:16+05:30",
      duration: null,
      mainContent: false,
    },
    {
      slug: "portfolio-fruzo-3",
      name: "A sachet you can turn in your hand — Case Study Video by Abox Agency",
      description: "Case study video for A sachet you can turn in your hand, produced by Abox Agency. Fruzo mobile product page on a phone.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/peru-powder.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fruzo/peru-power.png",
      uploadDate: "2026-09-01T08:47:46+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "portfolio-fruzo-4",
      name: "Flavour as motion, not a flat tile — Case Study Video by Abox Agency",
      description: "Case study video for Flavour as motion, not a flat tile, produced by Abox Agency. Fruzo Tulum Twist product video.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/tulum-twist.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fruzo/tulum-twist.png",
      uploadDate: "2026-09-01T08:47:46+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "portfolio-fruzo",
      name: "fruzo — Case Study Video by Abox Agency",
      description: "Case study video for fruzo, produced by Abox Agency. Fruzo Shopify storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/fruzo.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/fruzo/fruzo-poster.avif",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/giver-nutritions": [
    {
      slug: "portfolio-giver-nutritions",
      name: "Giver Nutritions — Shopify Case Study Video",
      description: "Case study video for Giver Nutritions, a brand in health & wellness supplements. Giver Nutritions focuses on mental-health supplements. Abox Agency paired Shopify development with branding and on-site SEO to help the label stand out. Covers the Shopify, UI/UX Design, Branding and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Giver.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/og_images/giver_nutritions.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/glosense": [
    {
      slug: "portfolio-glosense",
      name: "glosense — Case Study Video by Abox Agency",
      description: "Case study video for glosense, produced by Abox Agency. Part of the ecommerce design and development work delivered for glosense.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Glowsens.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/glosense/glosense1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/gogrub": [
    {
      slug: "portfolio-gogrub",
      name: "Go Grub — Shopify Case Study Video",
      description: "Case study video for Go Grub, a brand in snacks & pantry foods. Go Grub sells wholesome snacks, cookies, and pantry picks. Abox Agency built a flavour-forward Shopify store with custom branding and graphic design. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Go%20Grub.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/go_grub/gogrub1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/gpulla-reddy-sweets": [
    {
      slug: "portfolio-gpulla-reddy-sweets",
      name: "G.Pulla Reddy Sweets — Case Study Video by Abox Agency",
      description: "Case study video for G.Pulla Reddy Sweets, produced by Abox Agency. Part of the ecommerce design and development work delivered for G.Pulla Reddy Sweets.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/G.Pulla%20Reddy%20Sweets.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/g_pulla_reddy/gpulla1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/greenfay": [
    {
      slug: "portfolio-greenfay",
      name: "Greenfay — WordPress Development Case Study Video",
      description: "Case study video for Greenfay, a brand in agri-business & farming. Greenfay champions premium potato farming and community empowerment. Abox Agency built its WordPress site with new branding and logo design for the agri-business. Covers the WordPress Development, Branding and Logo Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Green%20Fay.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/greenfay/greenfay1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/healing-hands": [
    {
      slug: "portfolio-healing-hands",
      name: "Healing Hands — Shopify Case Study Video",
      description: "Case study video for Healing Hands, a brand in piles & digestive care. Healing Hands makes plant-based medicine for piles and constipation relief. Abox Agency built a discreet, trustworthy Shopify store with branding and on-site SEO. Covers the Shopify, UI/UX Design, Branding and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Healing%20Hands%20Final.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/og_images/healing_hands.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/healthoxide": [
    {
      slug: "portfolio-healthoxide",
      name: "Healthoxide — Shopify Case Study Video",
      description: "Case study video for Healthoxide, a brand in sports nutrition & fitness supplements. Healthoxide sells sports supplements and diet nutrition products. Abox Agency built its Shopify store with performance-focused branding and graphic design. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Health%20Oxide.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/healthoxide/healthoxide2.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/kutum-ayurveda": [
    {
      slug: "portfolio-kutum-ayurveda",
      name: "Kutum Ayurveda — Shopify Case Study Video",
      description: "Case study video for Kutum Ayurveda, a brand in ayurvedic skincare & cosmetics. Kutum Ayurveda blends ancient Ayurvedic care with modern skincare. Abox Agency built its Shopify store with custom branding and 3D product design for a premium feel. Covers the Shopify, UI/UX Design, Branding and 3D Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Kutum%20Ayurveda.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/kutum/kutum1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/label/elder": [
    {
      slug: "portfolio-label-elder",
      name: "3D (Label 2) — Case Study Video by Abox Agency",
      description: "Case study video for 3D, produced by Abox Agency. Part of the ecommerce design and development work delivered for 3D. Shown on the Elder project page at abox.agency/portfolio/label/elder.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20elder.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/elder/elder1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/label/molecule": [
    {
      slug: "portfolio-label-molecule",
      name: "Molecule 53 — 3D Rendering Case Study Video",
      description: "Case study video for Molecule 53, a brand in luxury skincare. Molecule 53 is a science-backed luxury skincare brand. Abox Agency created realistic 3D renders and product models to showcase it across every platform. Covers the 3D Rendering, Product Modeling and Product Visualization work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20molecule.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/molecule/molecule1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/label/moody": [
    {
      slug: "portfolio-label-moody",
      name: "3D — Case Study Video by Abox Agency",
      description: "Case study video for 3D, produced by Abox Agency. Part of the ecommerce design and development work delivered for 3D.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20moody.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/moody/moody1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/label/my-herb": [
    {
      slug: "portfolio-label-my-herb-1",
      name: "Label Myherb — Case Study Video by Abox Agency",
      description: "Case study video for Label Myherb, produced by Abox Agency. Part of the ecommerce design and development work delivered for Label Myherb.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20myherb.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/myherb/my-herb1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
    {
      slug: "portfolio-label-my-herb-2",
      name: "Label Myherb2 — Case Study Video by Abox Agency",
      description: "Case study video for Label Myherb2, produced by Abox Agency. Part of the ecommerce design and development work delivered for Label Myherb2.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20myherb2.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/myherb/my-herb1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/label/oval": [
    {
      slug: "portfolio-label-oval",
      name: "3D (Label 3) — Case Study Video by Abox Agency",
      description: "Case study video for 3D, produced by Abox Agency. Part of the ecommerce design and development work delivered for 3D. Shown on the Oval project page at abox.agency/portfolio/label/oval.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20oval.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/oval/oval1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/label/pourd-cocktails": [
    {
      slug: "portfolio-label-pourd-cocktails",
      name: "Pour'd — 3D Rendering Case Study Video",
      description: "Case study video for Pour'd, a brand in ready-to-drink cocktails. Pour'd makes ready-to-drink cocktails meant to be enjoyed effortlessly. Abox Agency created lifelike 3D bottle renders with sophisticated texturing and lighting. Covers the 3D Rendering, Product Modeling and Bottle Visualization work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20pourd.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/cocktails/cocktails1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/label/rare-suga": [
    {
      slug: "portfolio-label-rare-suga",
      name: "3D (Label) — Case Study Video by Abox Agency",
      description: "Case study video for 3D, produced by Abox Agency. Part of the ecommerce design and development work delivered for 3D. Shown on the Rare Suga project page at abox.agency/portfolio/label/rare-suga.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20raresuga.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/rare_suga/raresuga1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/label/ras-beauty": [
    {
      slug: "portfolio-label-ras-beauty-1",
      name: "Label Ras — Case Study Video by Abox Agency",
      description: "Case study video for Label Ras, a brand in farm-to-face skincare. RAS Luxury Skincare is India's first farm-to-face beauty brand. Abox Agency created photorealistic 3D renders and product visuals for its 100% natural skincare line. Covers the 3D Rendering, 3D Modeling and Product Visualization work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20ras.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/ras/ras1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
    {
      slug: "portfolio-label-ras-beauty-2",
      name: "Label Ras2 — Case Study Video by Abox Agency",
      description: "Case study video for Label Ras2, a brand in farm-to-face skincare. RAS Luxury Skincare is India's first farm-to-face beauty brand. Abox Agency created photorealistic 3D renders and product visuals for its 100% natural skincare line. Covers the 3D Rendering, 3D Modeling and Product Visualization work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20ras2.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/ras/ras1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/label/ras-lipstick": [
    {
      slug: "portfolio-label-ras-lipstick",
      name: "RAS Lipstick — 3D Rendering Case Study Video",
      description: "Case study video for RAS Lipstick, a brand in luxury beauty & cosmetics. RAS Luxury's eco-friendly lipstick line needed a premium look. Abox Agency created photorealistic 3D renders that showcase its elegant, sustainable design. Covers the 3D Rendering, Product Modeling and Product Visualization work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20ras-lipstick.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/ras/ras9.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/label/terahash-solutions": [
    {
      slug: "portfolio-label-terahash-solutions",
      name: "Terahash Solutions — 3D Rendering Case Study Video",
      description: "Case study video for Terahash Solutions, a brand in crypto mining hardware. Terahash Solutions builds ASIC crypto mining hardware. Abox Agency created realistic 3D renders that showcase the technology and detail behind every miner. Covers the 3D Rendering, Product Modeling and Product Visualization work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Label%20terahash.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/label/terahash/terahash1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/maa-and-baby": [
    {
      slug: "portfolio-maa-and-baby",
      name: "Maa & Baby — Shopify Case Study Video",
      description: "Case study video for Maa & Baby, a brand in baby & parenting products. Maa & Baby is a one-stop shop for parenting essentials. Abox Agency built a warm, easy-to-navigate Shopify store with branding and SEO tuned for new-parent shoppers. Covers the Shopify, UI/UX Design, Branding and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Maa%20&%20Baby.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/maababy/maababy1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/maadico": [
    {
      slug: "portfolio-maadico",
      name: "Maadico — Case Study Video by Abox Agency",
      description: "Case study video for Maadico, produced by Abox Agency. Part of the ecommerce design and development work delivered for Maadico.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Maa%20Dico%20Tamplate.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/maadico/maadico1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/mia": [
    {
      slug: "portfolio-mia",
      name: "Mia My Inner Ally — Shopify Case Study Video",
      description: "Case study video for Mia My Inner Ally, a brand in feminine hygiene products. Mia My Inner Ally makes organic cotton sanitary pads for comfort and safety. Abox Agency built its Shopify site with branding, graphics, and SEO for a confident launch. Covers the Shopify, Web Development and SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Mia.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/mia/mia1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/murari-sweets": [
    {
      slug: "portfolio-murari-sweets",
      name: "Murari Sweets — Shopify Case Study Video",
      description: "Case study video for Murari Sweets, a brand in confectionery & sweets. Murari Sweets offers a diverse range of authentic Indian sweets. Abox Agency redefined its online presence with custom UI/UX design and Shopify development. Covers the Shopify, UI/UX Design and Web Development work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Maruti%20Sweets.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/murari_sweet/murarisweet1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/my-red-palette": [
    {
      slug: "portfolio-my-red-palette",
      name: "My Red Palette — Case Study Video by Abox Agency",
      description: "Case study video for My Red Palette, produced by Abox Agency. Part of the ecommerce design and development work delivered for My Red Palette.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/My%20Red%20Palete%20Tamplate.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/red_palette/red_palette1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/myoa-cookies": [
    {
      slug: "portfolio-myoa-cookies",
      name: "Myoa Cookies — Shopify Case Study Video",
      description: "Case study video for Myoa Cookies, a brand in gourmet cookies & snacks. Myoa Cookies bakes luxury cookies with premium ingredients. Abox Agency designed a custom Figma-to-Shopify store with fresh branding to match its indulgent positioning. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Myoa%20Cookies.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/myoa_cookies/cookies1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/mypro": [
    {
      slug: "portfolio-mypro",
      name: "My Pro Sport Nutritions — Shopify Case Study Video",
      description: "Case study video for My Pro Sport Nutritions, a brand in sports nutrition & supplements. My Pro Sport Nutritions sells protein and performance supplements worldwide. Abox Agency delivered a Shopify store with label design, branding, and 3D product renders. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/My%20Pro.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/mypro/mypro1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/naturare": [
    {
      slug: "portfolio-naturare",
      name: "Naturare — Shopify Case Study Video",
      description: "Case study video for Naturare, a brand in natural wellness products. Naturare champions natural, empowering wellness products. Abox Agency built its Shopify store with custom branding and 3D renders that bring each product to life online. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Naturare.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/naturare/naturare1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/neo-roots": [
    {
      slug: "portfolio-neo-roots",
      name: "Neo Roots — Shopify Case Study Video",
      description: "Case study video for Neo Roots, a brand in plants & greenery. Neo Roots designs custom greenery for indoor and outdoor spaces. Abox Agency built a Shopify store with nature-inspired branding and integrations for easy ordering. Covers the Shopify, UI/UX Design, Branding and Integrations work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Neo%20roots.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/neoroots/neoroots1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/nurdle": [
    {
      slug: "portfolio-nurdle",
      name: "Nurdle — Shopify Case Study Video",
      description: "Case study video for Nurdle, a brand in kids' apparel. Nurdle sells colorful kids' underwear with surprise gifts inside. Abox Agency built a playful Shopify store with branding and graphics that appeal to parents and kids. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Nurdel%20Tamplate.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/nurdle/nurdle1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/om-sweets": [
    {
      slug: "portfolio-om-sweets",
      name: "Om Sweets & Snacks — Shopify Case Study Video",
      description: "Case study video for Om Sweets & Snacks, a brand in indian sweets & snacks. Om Sweets & Snacks offers authentic sweets for every Indian festival and occasion. Abox Agency built a Shopify store with warm branding and graphic design. Covers the Shopify, UI/UX Design, Branding and Graphic Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/OmSweet.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/om_sweets/omsweets1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/outer-world": [
    {
      slug: "portfolio-outer-world",
      name: "Outer World — Case Study Video by Abox Agency",
      description: "Case study video for Outer World, produced by Abox Agency. Part of the ecommerce design and development work delivered for Outer World.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Outer%20World.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/outerworld/outerworld1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/oval": [
    {
      slug: "portfolio-oval",
      name: "Discretion isn't a style choice — Case Study Video by Abox Agency",
      description: "Case study video for Discretion isn't a style choice, produced by Abox Agency. Oval Health desktop and mobile site preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/oval.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/oval/oval-screen.avif",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/paperclip-and-co": [
    {
      slug: "portfolio-paperclip-and-co",
      name: "Paperclip & Co — Custom Development Case Study Video",
      description: "Case study video for Paperclip & Co, a brand in social media & brand identity. Paperclip & Co champions women's creativity through social campaigns. Abox Agency built a custom site with UI/UX and brand design that amplifies its mission. Covers the Custom Development, UI/UX Design and Brand Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Paperclip%20&%20Co.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/paperclip/paperclip1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/radhey-sweets": [
    {
      slug: "portfolio-radhey-sweets",
      name: "Radhey Sweets — Shopify Case Study Video",
      description: "Case study video for Radhey Sweets, a brand in sweets & confectionery. Radhey Sweets wanted its heritage of authentic Indian sweets to shine online. Abox Agency built a Shopify store with fresh branding and SEO to grow D2C sales. Covers the Shopify, UI/UX Design, Branding and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Radhey%20Sweet.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/radhey_sweets/radhey_sweets2.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/repeat-gud": [
    {
      slug: "portfolio-repeat-gud",
      name: "Repeat Gud — Shopify Case Study Video",
      description: "Case study video for Repeat Gud, a brand in natural sauces & condiments. Repeat Gud makes all-natural, nutrient-packed sauces. Abox Agency built its Shopify site with custom development, on-site SEO, and integrations for ethical sourcing. Covers the Shopify, Web Development, On-Site SEO and Integrations work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Repeat%20Gud.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/repeat_gud/repeat1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/savani-jewellery": [
    {
      slug: "portfolio-savani-jewellery",
      name: "Savani Jewellery — Shopify Case Study Video",
      description: "Case study video for Savani Jewellery, a brand in fine jewellery manufacturing. Savani Jewellery makes elegant, sustainable diamond jewellery. Abox Agency redesigned its Shopify site with SEO and integrations to simplify international shopping. Covers the Shopify, Web Development, SEO and Integrations work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Savani.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/savani/savani1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/shistaka": [
    {
      slug: "portfolio-shistaka",
      name: "Shistaka — Shopify Case Study Video",
      description: "Case study video for Shistaka, a brand in herbal tea & wellness. Shistaka blends Ayurvedic wisdom with modern wellness in its herbal teas. Abox Agency built a Shopify store with custom branding and 3D renders for every product. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Shishtaka.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/shistaka/shistaka2.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/silverfied": [
    {
      slug: "portfolio-silverfied",
      name: "Silverfied — Case Study Video by Abox Agency",
      description: "Case study video for Silverfied, produced by Abox Agency. Part of the ecommerce design and development work delivered for Silverfied.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Silverfied.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/silverfied/silverfied1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/singlas-sweets": [
    {
      slug: "portfolio-singlas-sweets",
      name: "Singla Sweets — Shopify Case Study Video",
      description: "Case study video for Singla Sweets, a brand in confectionery & sweets. Singla Sweets makes authentic, handmade Indian delicacies. Abox Agency built a Shopify store with UI/UX design and web development as polished as the sweets themselves. Covers the Shopify, UI/UX Design and Web Development work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Singla's%20Sweets.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/singlas/singlas1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/spark-fusion": [
    {
      slug: "portfolio-spark-fusion",
      name: "Spark Fusion — Shopify Case Study Video",
      description: "Case study video for Spark Fusion, a brand in fitness & nutrition supplements. Spark Fusion sells premium fitness supplements for strength and health. Abox Agency built a Shopify store with 3D renders and infographics to boost conversion. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Sparkfusion.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/sparkfusion/sparkfusion1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/teatox-life": [
    {
      slug: "portfolio-teatox-life",
      name: "TeaTox Life — Shopify Case Study Video",
      description: "Case study video for TeaTox Life, a brand in herbal tea & wellness. TeaTox Life sells herbal wellness teas across the US. Abox Agency rebuilt its Shopify store with fresh branding and 3D product renders that lifted its global presence. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Tea%20Tox.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/og_images/teatox_life.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/technosport": [
    {
      slug: "portfolio-technosport",
      name: "Techno Sport — Shopify Case Study Video",
      description: "Case study video for Techno Sport, a brand in athleisure & sportswear. Techno Sport needed a seamless move from CartMax to Shopify without losing a single order. Abox Agency handled the migration, custom integrations, and on-site SEO. Covers the Shopify, Web Development and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Techno%20Sport%20Tamplate.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/technosport/technosport1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/terahash-solutions": [
    {
      slug: "portfolio-terahash-solutions",
      name: "Terahash Solutions — Webflow Development Case Study Video",
      description: "Case study video for Terahash Solutions, a brand in crypto mining devices. Terahash Solutions needed a website that matched the pace of crypto mining tech. Abox Agency built a sleek Webflow site with UI/UX design and 3D renders. Covers the Webflow Development, UI/UX Design, Logo Design and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Terahash%20Solution.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/terahash/terahash1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/tomahawk": [
    {
      slug: "portfolio-tomahawk",
      name: "Tomahawk — Case Study Video by Abox Agency",
      description: "Case study video for Tomahawk, produced by Abox Agency. Part of the ecommerce design and development work delivered for Tomahawk.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Tomahawk%20Tamplate.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/tomahawk/tomahawk1.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: false,
    },
  ],
  "/portfolio/ubar": [
    {
      slug: "portfolio-ubar",
      name: "Ubar — Shopify Case Study Video",
      description: "Case study video for Ubar, a brand in health supplements. Ubar makes premium health supplements and needed packaging that matched its Shopify storefront. Abox Agency delivered UI/UX design, packaging, and on-site SEO. Covers the Shopify, UI/UX Design, Packaging Design and On-Site SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Ubar.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/ubar/ubar2.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/uppercase": [
    {
      slug: "portfolio-uppercase",
      name: "Uppercase — Shopify Case Study Video",
      description: "Case study video for Uppercase, a brand in eco-friendly travel gear. Uppercase makes eco-friendly, stylish travel bags. Abox Agency migrated the brand to Shopify with fresh UI/UX design and SEO to support its sustainability story. Covers the Shopify, UI/UX Design, Branding and SEO work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/UpperCase.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/uppercase/uppercase1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/waylon-smith": [
    {
      slug: "portfolio-waylon-smith",
      name: "Eyewear is the hardest thing to sell online. — Case Study Video by Abox Agency",
      description: "Case study video for Eyewear is the hardest thing to sell online., produced by Abox Agency. Waylon Smith desktop and mobile storefront preview.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Case-Study/wayloan-smith.webm",
      thumbnailUrl: "https://abox.agency/assets/image/casestudy/waylon-smith/storefront.webp",
      uploadDate: "2026-09-01T08:47:22+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/weight-manage": [
    {
      slug: "portfolio-weight-manage",
      name: "Weight Manage — Shopify Case Study Video",
      description: "Case study video for Weight Manage, a brand in weight management & healthcare. Weight Manage sells supplements for effective, healthy weight management. Abox Agency built a Shopify store with custom branding and 3D renders for its product line. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/The%20Weight%20Manage.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/og_images/weight_manage.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/within-beauty": [
    {
      slug: "portfolio-within-beauty",
      name: "Within Beauty — Shopify Case Study Video",
      description: "Case study video for Within Beauty, a brand in cosmetics & skincare. Within Beauty makes cruelty-free skincare with natural ingredients. Abox Agency built a Shopify store with clean UI/UX and 3D renders to spotlight its formulas. Covers the Shopify, UI/UX Design, Branding and 3D Rendering work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Within%20Final%20Template.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/within/within1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/yo-gathiya": [
    {
      slug: "portfolio-yo-gathiya",
      name: "Yo Gathiya — Shopify Case Study Video",
      description: "Case study video for Yo Gathiya, a brand in namkeen & savory snacks. Yo Gathiya brings premium Gujarati namkeen online. Abox Agency built its Shopify store with custom branding and label design that carries the brand's tradition forward. Covers the Shopify, UI/UX Design, Branding and Label Design work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Yo%20Gathiya%20Tamplate.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/portfolios/yogathiya/yogathiya1.webp",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
  "/portfolio/yogabar": [
    {
      slug: "portfolio-yogabar",
      name: "Yogabar — Shopify Case Study Video",
      description: "Case study video for Yogabar, a brand in health food & nutrition. Yogabar, a leading protein-food brand, needed a Shopify site that could handle scale. Abox Agency handled UI/UX design, graphic design, and ongoing site maintenance. Covers the Shopify, UI/UX Design and Site Maintenance work delivered for the project.",
      contentUrl: "https://s3.in-west3.purestore.io/aboxstore/Yogabar.mp4",
      thumbnailUrl: "https://abox.agency/assets/image/og_images/yogabar.jpg",
      uploadDate: "2026-08-25T09:23:26+00:00",
      duration: null,
      mainContent: true,
    },
  ],
}

/** Videos for a route. Returns [] for unknown routes. */
export function getVideosForPath(pathname) {
  return portfolioVideosByPath[pathname] || []
}

/**
 * Only videos Google should be asked to index: main content, with a real
 * poster. Everything else stays out of JSON-LD and the sitemap.
 */
export function getIndexableVideos(pathname) {
  return getVideosForPath(pathname).filter(
    (v) => v.mainContent && v.thumbnailUrl && v.contentUrl
  )
}

/**
 * schema.org VideoObject for one registry entry.
 *
 * Deliberately omits embedUrl: we host the file directly and have no player
 * page. Google wants embedUrl OR contentUrl, and pointing embedUrl at a
 * non-player URL is an error — which is what the old hardcoded VideoObject in
 * Home.jsx did (embedUrl: "https://abox.agency/").
 */
export function buildVideoObject(video, pageUrl) {
  const schema = {
    "@type": "VideoObject",
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
    contentUrl: video.contentUrl,
    isFamilyFriendly: true,
    publisher: {
      "@type": "Organization",
      name: "Abox Agency",
      logo: {
        "@type": "ImageObject",
        url: "https://abox.agency/assets/image/logo.jpg",
      },
    },
  }
  if (video.duration) schema.duration = video.duration
  if (pageUrl) schema.mainEntityOfPage = { "@type": "WebPage", "@id": pageUrl }
  return schema
}
